/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { dashboardsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { resolveRange, toQuery, intervalFor } from './dashboardModel'
import { BarView, DataTableView, NumberView, TopListView, TrendView } from './WidgetViews'
import Loading from '../common/Loading'

/** Builds a list URL with the widget's filters plus one extra exact/empty filter. */
const listUrl = (widget, extra) => {
  const params = new URLSearchParams()
  Object.entries({ ...(widget.filters || {}), ...extra }).forEach(([key, ops]) =>
    Object.entries(ops).forEach(([op, value]) => params.append(op === 'eq' ? `filter.${key}` : `filter.${key}.${op}`, value)))
  const qs = params.toString()
  return `/records/${widget.entityType}${qs ? `?${qs}` : ''}`
}

const WidgetCard = ({ widget, title, rangeId, editing, onEdit, onRemove, onMove, isFirst, isLast }) => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const [asTable, setAsTable] = useState(false)
  const query = toQuery(widget, rangeId)
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [tenantId, 'records', 'widget', query],
    queryFn: () => dashboardsAPI.widgetData(query),
    // refetch keeps the frame, dimmed, but only while the widget kind is unchanged: a NUMBER
    // result has no series to draw as a BAR
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey?.[3]?.kind === query.kind ? keepPreviousData(previous) : undefined),
    retry: false,
  })
  const chart = widget.kind === 'BAR' || widget.kind === 'TREND'

  const openGroup = (bucket) => {
    if (bucket.key === '__other__') return
    navigate(listUrl(widget, { [widget.groupBy]: bucket.key === null ? { empty: 'true' } : { eq: bucket.key } }))
  }

  let body
  if (isLoading) body = <Loading />
  else if (error) body = <Alert severity="warning">{getErrorMessage(error)} {getErrorList(error).join('; ')}</Alert>
  else if (asTable && chart) body = <DataTableView data={data} kind={widget.kind} interval={query.interval} />
  else if (widget.kind === 'NUMBER') body = <NumberView data={data} compare={query.compare} rangeLabel={widget.dateField === 'none' ? 'All time' : resolveRange(rangeId).label} />
  else if (widget.kind === 'BAR') body = <BarView data={data} onSelect={editing ? null : openGroup} />
  else if (widget.kind === 'TREND') body = <TrendView data={data} interval={query.interval || intervalFor(rangeId)} />
  else body = <TopListView data={data} />

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', borderStyle: editing ? 'dashed' : 'solid' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ flexGrow: 1, cursor: editing ? 'default' : 'pointer', '&:hover': editing ? undefined : { textDecoration: 'underline' } }}
          onClick={editing ? undefined : () => navigate(listUrl(widget, {}))}
          title={editing ? undefined : 'Open these records'}
        >
          {title}
        </Typography>
        {chart && !editing && (
          <Tooltip title={asTable ? 'Show chart' : 'Show as table'}>
            <IconButton onClick={() => setAsTable((v) => !v)} aria-label={asTable ? 'Show chart' : 'Show as table'}>
              {asTable ? <InsertChartOutlinedIcon fontSize="small" /> : <TableChartOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {editing && (
          <>
            <IconButton disabled={isFirst} onClick={() => onMove(-1)} aria-label="Move earlier"><ArrowBackIcon fontSize="small" /></IconButton>
            <IconButton disabled={isLast} onClick={() => onMove(1)} aria-label="Move later"><ArrowForwardIcon fontSize="small" /></IconButton>
            <IconButton onClick={onEdit} aria-label="Edit widget"><EditOutlinedIcon fontSize="small" /></IconButton>
            <IconButton onClick={onRemove} aria-label="Remove widget"><DeleteOutlineIcon fontSize="small" /></IconButton>
          </>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, opacity: isFetching && !isLoading ? 0.6 : 1, transition: 'opacity .2s' }}>{body}</Box>
    </Paper>
  )
}

export default WidgetCard
